import { Button, type ButtonProps } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import Link from 'next/link'
import React from 'react'

import type { Page } from '@/payload-types'

type CMSLinkType = {
  appearance?: 'inline' | ButtonProps['variant']
  children?: React.ReactNode
  className?: string
  label?: string | null
  newTab?: boolean | null
  reference?: {
    relationTo: 'pages'
    value: Page | string | number
  } | null
  size?: ButtonProps['size'] | null
  type?: 'custom' | 'reference' | null
  url?: string | null
}

export function CMSLink({
  type,
  appearance = 'inline',
  children,
  className,
  label,
  newTab,
  reference,
  size: sizeFromProps,
  url,
}: Readonly<CMSLinkType>): React.ReactNode {
  const isReference =
    type === 'reference' &&
    typeof reference?.value === 'object' &&
    reference?.value &&
    'slug' in reference.value

  let href = url
  if (isReference && reference) {
    const relationToPrefix = reference.relationTo === 'pages' ? '' : `/${reference.relationTo}`
    const slug = (reference.value as Page).slug
    if (slug === 'home') {
      href = `${relationToPrefix}/`
    } else {
      href = `${relationToPrefix}/${slug}`
    }
  }

  if (!href) return null

  const size = appearance === 'link' ? undefined : sizeFromProps
  const newTabProps = newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}
  const content = label ?? children

  if (appearance === 'inline') {
    return (
      <Link className={cn(className)} href={href} {...newTabProps}>
        {content}
      </Link>
    )
  }

  return (
    <Button asChild className={className} size={size} variant={appearance}>
      <Link className={cn(className)} href={href} {...newTabProps}>
        {content}
      </Link>
    </Button>
  )
}
